import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import InputAdornment from '@mui/material/InputAdornment';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  patchRequest,
  getErrorMessage,
  getRequest,
  MANUFACTURING_API,
  postRequest,
} from "./api";
import {
  EmptyStateCard,
  PageShell,
  SectionCard,
  panelSx,
  statusChip,
  controlDeskSx,
  metricTileSx
} from "./ManufacturingCommon";

const emptyForm = {
  order_code: "",
  partner_id: "",
  manufacturing_product_id: "",
  purchase_order_number: "",
  customer_reference: "",
  target_quantity: "",
  planned_start_date: "",
  planned_end_date: "",
  component_issue_notes: "",
  base_os_version: "",
  test_plan_version: "",
  notes: "",
};

const ORDER_STATUS_OPTIONS = [
  "DRAFT",
  "APPROVED",
  "RELEASED",
  "IN_PRODUCTION",
  "PARTIALLY_COMPLETED",
  "COMPLETED",
  "CLOSED",
  "CANCELLED",
];

const emptyFilters = {
  status: "",
  partner_id: "",
  q: "",
};

function ManufacturingOrdersSkeleton() {
  const orderSkeletons = Array.from({ length: 4 });
  const metricSkeletons = Array.from({ length: 4 });

  return (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      {orderSkeletons.map((_, index) => (
        <Box sx={{ width: "100%" }} key={index}>
          <Box sx={{ ...panelSx, overflow: "hidden" }}>
            <Box sx={{ p: 2.4, borderBottom: "1px solid rgba(226,232,240,0.9)" }}>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="28%" height={30} />
                  <Skeleton variant="text" width="52%" height={22} />
                </Box>
                <Skeleton variant="rounded" width={112} height={28} sx={{ borderRadius: 999 }} />
              </Stack>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  xl: "minmax(0, 1fr) 320px",
                },
                minHeight: 220,
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: 1.25,
                  }}
                >
                  {metricSkeletons.map((__, metricIndex) => (
                    <Box
                      key={metricIndex}
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        backgroundColor: "#F8FAFC",
                        border: "1px solid rgba(226,232,240,0.95)",
                      }}
                    >
                      <Skeleton variant="text" width="46%" height={18} />
                      <Skeleton variant="text" width="62%" height={26} sx={{ mt: 0.85 }} />
                    </Box>
                  ))}
                </Box>
                <Skeleton variant="rounded" width="100%" height={80} sx={{ mt: 1.5, borderRadius: 2.5 }} />
              </Box>
              <Box
                sx={{
                  p: 2.5,
                  borderTop: { xs: "1px solid rgba(226,232,240,0.9)", xl: "none" },
                  borderLeft: { xl: "1px solid rgba(226,232,240,0.9)" },
                  backgroundColor: "#F8FAFC",
                }}
              >
                <Skeleton variant="text" width="44%" height={18} />
                <Skeleton variant="text" width="72%" height={24} sx={{ mt: 1 }} />
                <Skeleton variant="rounded" width="100%" height={38} sx={{ mt: 2, borderRadius: 2 }} />
                <Skeleton variant="rounded" width="100%" height={38} sx={{ mt: 1, borderRadius: 2 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      ))}
    </Grid>
  );
}

export default function ManufacturingOrders() {
  const navigate = useNavigate();
  const { activeOrg } = useAuth();
  const token = localStorage.getItem("authToken");
  const [orders, setOrders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [cancleloder, setCancleloder] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openAllocate, setOpenAllocate] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [allocateQuantity, setAllocateQuantity] = useState("");
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [releasingId, setReleasingId] = useState(null); // Loader for Release
  const [cancellingId, setCancellingId] = useState(null); // Loader for Cancel
  const [releaseConfirmOpen, setReleaseConfirmOpen] = useState(false);
const [orderToRelease, setOrderToRelease] = useState(null);
  const [errors, setErrors] = useState({});
  const [filters, setFilters] = useState(emptyFilters);
  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const releasedCount = orders.filter((order) => order.status === "RELEASED").length;
  const inProductionCount = orders.filter((order) =>
    ["IN_PRODUCTION", "PARTIALLY_COMPLETED"].includes(order.status),
  ).length;
  const plannedUnits = orders.reduce(
    (acc, order) => acc + Number(order.target_quantity || 0),
    0,
  );
  const loadData = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    setLoading(true);
    setError("");
    try {
      const orderParams = {};
      if (filters.status) orderParams.status = filters.status;
      if (filters.partner_id) orderParams.partner_id = filters.partner_id;
      if (filters.q.trim()) orderParams.q = filters.q.trim();

      const [ordersData, partnersData, productsData] = await Promise.all([
        getRequest(`${MANUFACTURING_API}/orders`, token, activeOrg, {
          params: orderParams,
        }),
        getRequest(`${MANUFACTURING_API}/partners`, token, activeOrg),
        getRequest(`${MANUFACTURING_API}/products`, token, activeOrg),
      ]);
      setOrders(ordersData?.orders || []);
      setPartners(partnersData?.partners || []);
      setProducts(productsData?.products || []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load manufacturing orders"));
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectedProduct = useMemo(
    () =>
      products.find((item) => item.id === form.manufacturing_product_id) ||
      null,
    [products, form.manufacturing_product_id],
  );

  useEffect(() => {
    if (!selectedProduct) return;
    setForm((prev) => ({
      ...prev,
      base_os_version:
        prev.base_os_version || selectedProduct.default_base_os_version || "",
      test_plan_version:
        prev.test_plan_version ||
        selectedProduct.default_test_plan_version ||
        "",
    }));
  }, [selectedProduct]);

  const handleCreate = async () => {
    const newErrors = {};

    if (!form.order_code.trim()) newErrors.order_code = "Required";
    if (!form.partner_id) newErrors.partner_id = "Required";
    if (!form.manufacturing_product_id)
      newErrors.manufacturing_product_id = "Required";
    if (!form.target_quantity) newErrors.target_quantity = "Required";
    if (
      form.target_quantity &&
      (!Number.isFinite(Number(form.target_quantity)) ||
        Number(form.target_quantity) <= 0)
    ) {
      newErrors.target_quantity = "Target quantity must be greater than 0";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setSaving(true);
    setError("");

    try {
      await postRequest(
        `${MANUFACTURING_API}/orders`,
        {
          ...form,
          order_code: form.order_code.trim().toUpperCase(),
          target_quantity: Number(form.target_quantity),
        },
        token,
        activeOrg,
      );

      setSuccess("Manufacturing order created");
      setOpen(false);
      setForm(emptyForm);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, "Unable to create manufacturing order"));
    } finally {
      setSaving(false);
    }
  };

 const processReleaseOrder = async () => {
  if (!orderToRelease) return;

  setReleasingId(orderToRelease.id);

  try {
    await postRequest(
      `${MANUFACTURING_API}/orders/${orderToRelease.id}/release`,
      {},
      token,
      activeOrg,
    );

    setSuccess("Manufacturing order released");
    await loadData();

    setReleaseConfirmOpen(false);
  } catch (err) {
    setError(getErrorMessage(err, "Unable to release manufacturing order"));
  } finally {
    setReleasingId(null);
    setOrderToRelease(null);
  }
};

  const handleOpenEdit = (order) => {
    setEditingOrderId(order.id);
    setEditForm({
      order_code: order.order_code || "",
      partner_id: order.partner_id || order.partner?.id || "",
      manufacturing_product_id:
        order.manufacturing_product_id || order.product?.id || "",
      purchase_order_number: order.purchase_order_number || "",
      customer_reference: order.customer_reference || "",
      target_quantity: order.target_quantity || "",
      planned_start_date: order.planned_start_date || "",
      planned_end_date: order.planned_end_date || "",
      component_issue_notes: order.component_issue_notes || "",
      base_os_version: order.base_os_version || "",
      test_plan_version: order.test_plan_version || "",
      notes: order.notes || "",
      status: order.status || "DRAFT",
    });
    setOpenEdit(true);
  };

  const handleUpdateOrder = async () => {
    if (!editingOrderId) return;
    if (
      !editForm.order_code.trim() ||
      !editForm.partner_id ||
      !editForm.manufacturing_product_id ||
      !editForm.target_quantity
    ) {
      setError(
        "Order code, partner, product, and target quantity are required",
      );
      return;
    }
    const parsedTargetQuantity = Number(editForm.target_quantity);
    if (!Number.isFinite(parsedTargetQuantity) || parsedTargetQuantity <= 0) {
      setError("Target quantity must be greater than 0");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await patchRequest(
        `${MANUFACTURING_API}/orders/${editingOrderId}`,
        {
          ...editForm,
          order_code: editForm.order_code.trim().toUpperCase(),
          target_quantity: parsedTargetQuantity,
        },
        token,
        activeOrg,
      );
      setSuccess("Manufacturing order updated");
      setOpenEdit(false);
      setEditingOrderId(null);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, "Unable to update manufacturing order"));
    } finally {
      setSaving(false);
    }
  };

  // Isse purane handleCancelOrder se replace karein
  const triggerCancelWarning = (order) => {
    setOrderToCancel(order);
    setCancelConfirmOpen(true);
  };
  const triggerReleaseWarning = (order) => {
  setOrderToRelease(order);
  setReleaseConfirmOpen(true);
};

  // Yeh actual cancel karne wala function hoga
  const processCancelOrder = async () => {
    if (!orderToCancel) return;

    setCancellingId(orderToCancel.id); // ✅ loader start

    try {
      await postRequest(
        `${MANUFACTURING_API}/orders/${orderToCancel.id}/cancel`,
        {},
        token,
        activeOrg,
      );

      setSuccess("Manufacturing order cancelled");
      await loadData();

      setCancelConfirmOpen(false); // ✅ close AFTER success
    } catch (err) {
      setError(getErrorMessage(err, "Unable to cancel manufacturing order"));
    } finally {
      setCancellingId(null); // ✅ loader stop
      setOrderToCancel(null);
    }
  };

  const handleOpenAllocate = (order) => {
    const remaining = Math.max(
      0,
      Number(order.target_quantity || 0) -
      Number(order.summary?.allocated_serials || 0),
    );
    setSelectedOrder(order);
    setAllocateQuantity(remaining > 0 ? String(remaining) : "1");
    setOpenAllocate(true);
  };

  const handleAllocate = async () => {
    if (!selectedOrder) return;
    const quantity = Number(allocateQuantity);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setError("Please enter a valid positive quantity");
      return;
    }
    try {
      await postRequest(
        `${MANUFACTURING_API}/orders/${selectedOrder.id}/allocate-serials`,
        { quantity },
        token,
        activeOrg,
      );
      setSuccess("Serial numbers allocated");
      setOpenAllocate(false);
      setSelectedOrder(null);
      setAllocateQuantity("");
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, "Unable to allocate serials"));
    }
  };

  const handleApplyFilters = () => {
    setFilters({
      status: draftFilters.status,
      partner_id: draftFilters.partner_id,
      q: draftFilters.q,
    });
  };

  const handleClearFilters = () => {
    setDraftFilters(emptyFilters);
    setFilters(emptyFilters);
  };

  return (
    <PageShell
      title="Manufacturing Orders"
      subtitle="Raise production orders, assign partners and products, release them to the floor, and control serial allocation from a cleaner production-focused workspace."
      actions={
        <Stack spacing={1.25}>
          {/* <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: "rgba(226,232,240,0.72)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Production Control
          </Typography> */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setErrors({});
              setForm(emptyForm);
              setOpen(true);
            }}
            sx={{
               px: 2.2,
                      py: 1.1,
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                       whiteSpace: "nowrap",
                      color: "#082F49",
                      background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                      boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
                    }}
          >
            New Order
          </Button>
        </Stack>
      }
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}

      <Paper elevation={0} sx={controlDeskSx}>
        <Stack
          direction={{ xs: "column", xl: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", xl: "center" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: {xs:10,md:15}, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748B" }}>
              Production Queue
            </Typography>
            <Typography sx={{ mt: 0.75, fontSize: 15, color: "#334155", lineHeight: 1.7 }}>
              Order planning, release control, and serial generation in a denser production board.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ minWidth: { xl: 560 } }}>
            {[
              ["Orders", orders.length],
              ["Released", releasedCount],
              ["In production", inProductionCount],
              ["Units planned", plannedUnits],
            ].map(([label, value]) => (
              <Box key={label} sx={{ ...metricTileSx, flex: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  {label}
                </Typography>
                <Typography sx={{ mt: 0.65, fontSize: 24, fontWeight: 900, color: "#0F172A", letterSpacing: "-0.04em" }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1.25}
          alignItems={{ xs: "stretch", lg: "center" }}
          sx={{ mt: 2 }}
        >
        <TextField  
  fullWidth
  label="Search"
  placeholder="Order code, PO number"
   //or customer reference
  
  value={filters.q || ''} 
  onChange={(e) => {
    const value = e.target.value;
   
    setFilters((prev) => ({ ...prev, q: value }));
    
   
  }}
  sx={{ minWidth: { lg: 150 } }}
  InputProps={{ 
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon color="action" />
      </InputAdornment>
    ),
    endAdornment: filters.q ? (
      <InputAdornment position="end">
        <IconButton
          aria-label="clear search"
          onClick={() => {
            setFilters((prev) => ({ ...prev, q: '' }));
            
          }}
          edge="end"
          size="small"
        >
          <ClearIcon />
        </IconButton>
      </InputAdornment>
    ) : null,
  }}
/>
         <TextField
  select
  fullWidth
  label="Status"
  
  value={filters.status || ''} 
  onChange={(e) => {
    const value = e.target.value;
    setFilters((prev) => ({ ...prev, status: value }));
  }}
  sx={{ minWidth: { lg: 150 } }}
>
  <MenuItem value="">All statuses</MenuItem>
  {ORDER_STATUS_OPTIONS.map((status) => (
    <MenuItem key={status} value={status}>
      {status}
    </MenuItem>
  ))}
</TextField>
          <TextField
  select
  fullWidth
  label="Partner"
  value={filters.partner_id || ''}
  onChange={(e) => {
    const value = e.target.value;
    setFilters((prev) => ({
      ...prev,
      partner_id: value,
    }));
  }}
  sx={{ minWidth: { lg: 150 } }}
>
  <MenuItem value="">All partners</MenuItem>
  {partners.map((partner) => (
    <MenuItem key={partner.id} value={partner.id}>
      {partner.name}
    </MenuItem>
  ))}
</TextField>
          <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", lg: "auto" } }}>
            
            {(
              draftFilters?.status ||
              draftFilters?.partner_id ||
              draftFilters?.q?.trim()
            ) && (
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  sx={{ minWidth: 110, fontWeight: 800 }}
                >
                  Clear
                </Button>
              )}
          </Stack>
        </Stack>
      </Paper>

      {loading ? (
        <ManufacturingOrdersSkeleton />
      ) : (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {orders.map((order) => {
            const remaining = Math.max(
              0,
              Number(order.target_quantity || 0) -
              Number(order.summary?.allocated_serials || 0),
            );
            return (
              <Box sx={{ width: "100%" }} key={order.id}>
                <SectionCard
                  title={order.order_code}
                  subtitle={`${order.partner?.name || "Partner pending"} | ${order.product?.name || "Product pending"}`}
                  action={statusChip(order.status)}
                  contentSx={{ p: 0 }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        xl: "minmax(0, 1fr) 320px",
                      },
                      minHeight: 220,
                    }}
                  >
                    <Box sx={{ p: 2.5 }}>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "repeat(2, minmax(0, 1fr))",
                            lg: "repeat(4, minmax(0, 1fr))",
                          },
                          gap: 1.25,
                        }}
                      >
                        {[
                          ["Target qty", order.target_quantity],
                          ["Allocated", order.summary?.allocated_serials || 0],
                          ["Converted", order.summary?.units_converted || 0],
                          ["Remaining", remaining],
                        ].map(([label, value]) => (
                          <Box
                            key={label}
                            sx={{
                              p: 1.5,
                              borderRadius: 2.5,
                              backgroundColor: "#F8FAFC",
                              border: "1px solid rgba(226,232,240,0.95)",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 800,
                                color: "#64748B",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                              }}
                            >
                              {label}
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.85,
                                fontSize: 22,
                                fontWeight: 900,
                                color: "#0F172A",
                                letterSpacing: "-0.04em",
                              }}
                            >
                              {value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      <Box
                        sx={{
                          mt: 1.5,
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "repeat(2, minmax(0, 1fr))",
                          },
                          gap: 1.25,
                        }}
                      >
                        {[
                          [
                            "Purchase order",
                            order.purchase_order_number || "Not set",
                          ],
                          [
                            "Customer reference",
                            order.customer_reference || "Not set",
                          ],
                        ].map(([label, value]) => (
                          <Box
                            key={label}
                            sx={{
                              p: 1.5,
                              borderRadius: 2.5,
                              backgroundColor: "#F8FAFC",
                              border: "1px solid rgba(226,232,240,0.95)",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 800,
                                color: "#64748B",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                              }}
                            >
                              {label}
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.85,
                                fontSize: 14,
                                fontWeight: 700,
                                color: "#334155",
                              }}
                            >
                              {value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: 2.5,
                        borderTop: {
                          xs: "1px solid rgba(226,232,240,0.9)",
                          xl: "none",
                        },
                        borderLeft: { xl: "1px solid rgba(226,232,240,0.9)" },
                        backgroundColor: "#F8FAFC",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 800,
                          color: "#64748B",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}
                      >
                        Actions
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.75,
                          fontSize: 14,
                          color: "#475569",
                          lineHeight: 1.7,
                        }}
                      >
                        Open the order, update planning details, generate
                        serials, or change the production state.
                      </Typography>

                      <Stack
                        direction={{ xs: "column", md: "row", xl: "column" }}
                        spacing={1.2}
                        sx={{ mt: 2 }}
                      >
                        <Button
                          fullWidth
                          size="medium"
                          variant="contained"
                          startIcon={<OpenInNewIcon />}
                          onClick={() =>
                            navigate(`/manufacturing/orders/${order.id}`)
                          }
                          sx={{ fontWeight: 800 }}
                        >
                          Open Order
                        </Button>
                        <Button
                          fullWidth
                          size="medium"
                          variant="outlined"
                          onClick={() => handleOpenEdit(order)}
                          sx={{ fontWeight: 800 }}
                        >
                          Edit Order
                        </Button>
                        <Button
                          fullWidth
                          size="medium"
                          variant="outlined"
                          startIcon={<QrCode2Icon />}
                          onClick={() => handleOpenAllocate(order)}
                          sx={{ fontWeight: 800 }}
                        >
                          Generate Serials
                        </Button>
                        <Button
                          fullWidth
                          size="medium"
                          variant="outlined"
                          startIcon={releasingId === order.id ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                          onClick={() => triggerReleaseWarning(order)}
                          disabled={
                            releasingId === order.id ||
                            !["DRAFT", "APPROVED"].includes(order.status)
                          }
                          sx={{ fontWeight: 800 }}
                        >
                          {releasingId === order.id ? "Releasing..." : "Release Order"}
                        </Button>
                        <Button
                          fullWidth
                          size="medium"
                          color="error"
                          variant="outlined"
                          startIcon={cancellingId === order.id ? <CircularProgress size={16} color="inherit" /> : null}
                          onClick={() => triggerCancelWarning(order)}
                          disabled={
                            cancellingId === order.id ||
                            Number(order.summary?.allocated_serials || 0) > 0 ||
                            ["CANCELLED", "CLOSED"].includes(order.status)
                          }
                          sx={{ fontWeight: 800 }}
                        >
                          {cancellingId === order.id ? "Cancelling..." : "Cancel Order"}
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                </SectionCard>
              </Box>
            );
          })}

          {!orders.length ? (
            <Grid item xs={12} sx={{ width: '100%' }}>
              <EmptyStateCard
                title="No manufacturing orders yet"
                description="Create a production order once partners and products are qualified and ready for release."
              />
            </Grid>
          ) : null}
        </Grid>
      )}

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setErrors({});
          setForm(emptyForm);
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: panelSx }}
      >
        <DialogTitle
          sx={{
            background:
               "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          color: "#fff",
          boxShadow: "0 28px 60px rgba(15,23,42,0.18)",
          }}
        >
          Create Manufacturing Order
          {/* CLOSE ICON */}
          <IconButton
            onClick={() => {
              setOpen(false);
              setErrors({});
              setForm(emptyForm);
            }}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#F8FAFC",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Order Code"
                  required
                  value={form.order_code}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, order_code: e.target.value }));
                    setErrors((prev) => ({ ...prev, order_code: "" }));
                  }}
                  error={!!errors.order_code}
                  helperText={errors.order_code || ""}
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Target Quantity"
                  required
                  type="number"
                  value={form.target_quantity}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, target_quantity: e.target.value }));
                    setErrors((prev) => ({ ...prev, target_quantity: "" }));
                  }}
                  error={!!errors.target_quantity}
                  helperText={errors.target_quantity || ""}
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  select
                  fullWidth
                  label="Partner"
                  required
                  value={form.partner_id}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, partner_id: e.target.value }));
                    setErrors((prev) => ({ ...prev, partner_id: "" }));
                  }}
                  error={!!errors.partner_id}
                  helperText={errors.partner_id || ""}
                >
                  {partners.map((partner) => (
                    <MenuItem key={partner.id} value={partner.id}>
                      {partner.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  select
                  fullWidth
                  label="Manufacturing Product"
                  required
                  value={form.manufacturing_product_id}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, manufacturing_product_id: e.target.value }));
                    setErrors((prev) => ({ ...prev, manufacturing_product_id: "" }));
                  }}
                  error={!!errors.manufacturing_product_id}
                  helperText={errors.manufacturing_product_id || ""}
                >
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Purchase Order Number"
                  value={form.purchase_order_number}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      purchase_order_number: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Customer Reference"
                  value={form.customer_reference}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      customer_reference: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Planned Start Date"
                  InputLabelProps={{ shrink: true }}
                  value={form.planned_start_date}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      planned_start_date: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Planned End Date"
                  InputLabelProps={{ shrink: true }}
                  value={form.planned_end_date}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      planned_end_date: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Base OS Version"
                  value={form.base_os_version}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      base_os_version: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Test Plan Version"
                  value={form.test_plan_version}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      test_plan_version: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: "100%" }}>
                <TextField
                  fullWidth
                  label="Component Issue Notes"
                  value={form.component_issue_notes}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      component_issue_notes: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: "100%" }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Notes"
                  value={form.notes}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, notes: e.target.value }))
                  }
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
         <Button
  variant="contained"
  onClick={handleCreate}
  disabled={saving}
  sx={{
    minWidth: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundImage: "linear-gradient(135deg, #FDE68A, #F59E0B)",
    // color: "#000", // Optional: Added text color for better contrast against the yellow/orange gradient
    "&:hover": {
      backgroundImage: "linear-gradient(135deg, #FCD34D, #D97706)", // Optional: Slightly darker gradient on hover
    }
  }}
>
  {saving ? (
    <CircularProgress size={20} sx={{ color: "white" }} />
  ) : (
    "Create Order"
  )}
</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          setEditingOrderId(null);
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: panelSx }}
      >
        <DialogTitle
          sx={{
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          color: "#fff",
          boxShadow: "0 28px 60px rgba(15,23,42,0.18)",
          }}
        >
          Edit Manufacturing Order
          <IconButton
                      onClick={() => {
                        setOpenEdit(false);
                      }}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "#F8FAFC",
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Order Code"
                  value={editForm.order_code}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      order_code: e.target.value.toUpperCase(),
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Target Quantity"
                  type="number"
                  value={editForm.target_quantity}
                  onChange={(e) =>
                    setEditForm((p) => ({
                      ...p,
                      target_quantity: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  select
                  fullWidth
                  label="Partner"
                  value={editForm.partner_id}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, partner_id: e.target.value }))
                  }
                >
                  {partners.map((partner) => (
                    <MenuItem key={partner.id} value={partner.id}>
                      {partner.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  select
                  fullWidth
                  label="Manufacturing Product"
                  value={editForm.manufacturing_product_id}
                  onChange={(e) =>
                    setEditForm((p) => ({
                      ...p,
                      manufacturing_product_id: e.target.value,
                    }))
                  }
                >
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={editForm.status || "DRAFT"}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, status: e.target.value }))
                  }
                >
                  {ORDER_STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Purchase Order Number"
                  value={editForm.purchase_order_number}
                  onChange={(e) =>
                    setEditForm((p) => ({
                      ...p,
                      purchase_order_number: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Customer Reference"
                  value={editForm.customer_reference}
                  onChange={(e) =>
                    setEditForm((p) => ({
                      ...p,
                      customer_reference: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Planned Start Date"
                  InputLabelProps={{ shrink: true }}
                  value={editForm.planned_start_date}
                  onChange={(e) =>
                    setEditForm((p) => ({
                      ...p,
                      planned_start_date: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Planned End Date"
                  InputLabelProps={{ shrink: true }}
                  value={editForm.planned_end_date}
                  onChange={(e) =>
                    setEditForm((p) => ({
                      ...p,
                      planned_end_date: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Base OS Version"
                  value={editForm.base_os_version}
                  onChange={(e) =>
                    setEditForm((p) => ({
                      ...p,
                      base_os_version: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Test Plan Version"
                  value={editForm.test_plan_version}
                  onChange={(e) =>
                    setEditForm((p) => ({
                      ...p,
                      test_plan_version: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: "100%" }}>
                <TextField
                  fullWidth
                  label="Component Issue Notes"
                  value={editForm.component_issue_notes}
                  onChange={(e) =>
                    setEditForm((p) => ({
                      ...p,
                      component_issue_notes: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: "100%" }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Notes"
                  value={editForm.notes}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, notes: e.target.value }))
                  }
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => {
              setOpenEdit(false);
              setEditingOrderId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateOrder}
            disabled={saving}
            sx={{
              minWidth: 140,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
                background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                      boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
            }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "Update Order"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAllocate}
        onClose={() => {
          setOpenAllocate(false);
          setSelectedOrder(null);
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: panelSx }}
      >
        <DialogTitle
          sx={{
            background:
               "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          color: "#fff",
          boxShadow: "0 28px 60px rgba(15,23,42,0.18)",
          }}
        >
          Generate Serial Numbers
           <IconButton
                      onClick={() => {
                        setOpenAllocate(false);
                      }}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "#F8FAFC",
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              {selectedOrder ? (
                <>
                  Order <strong>{selectedOrder.order_code}</strong> has target{" "}
                  <strong>{selectedOrder.target_quantity}</strong> with{" "}
                  <strong>
                    {selectedOrder.summary?.allocated_serials || 0}
                  </strong>{" "}
                  already allocated.
                </>
              ) : null}
            </Alert>
            <TextField
              fullWidth
              label="Quantity to Generate"
              type="number"
              value={allocateQuantity}
              onChange={(e) => setAllocateQuantity(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => {
              setOpenAllocate(false);
              setSelectedOrder(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<QrCode2Icon />}
            onClick={handleAllocate}
            sx={{
              minWidth: 140,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
                background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                      boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
            }}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#ef4444" }}>
          Confirm Cancellation
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel order <strong>{orderToCancel?.order_code}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button
            onClick={() => setCancelConfirmOpen(false)}
            variant="outlined"
            disabled={cancellingId === orderToCancel?.id}
            sx={{ color: "#0d0d0d", borderColor: "#0a0a0a" }}
          >
            No, Keep it
          </Button>
          <Button
            onClick={processCancelOrder}
            variant="contained"
            color="error"
            disabled={cancellingId === orderToCancel?.id}
            sx={{ fontWeight: 800, minWidth: 180 }}
          >
            {cancellingId === orderToCancel?.id ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Yes, Cancel order"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
  open={releaseConfirmOpen}
  onClose={() => setReleaseConfirmOpen(false)}
  PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
>
  <DialogTitle sx={{ fontWeight: 800, color: "#2563eb" }}>
    Confirm Release
  </DialogTitle>

  <DialogContent>
    <Typography>
      Are you sure you want to release order{" "}
      <strong>{orderToRelease?.order_code}</strong>?
    </Typography>
  </DialogContent>

  <DialogActions sx={{ pb: 2, px: 3 }}>
    <Button
      onClick={() => setReleaseConfirmOpen(false)}
      variant="outlined"
      disabled={releasingId === orderToRelease?.id}
      sx={{ color: "#202020", borderColor: "#0d0d0d" }}
    >
      No, keep it
    </Button>

    <Button
      onClick={processReleaseOrder}
      variant="contained"
      disabled={releasingId === orderToRelease?.id}
      sx={{ fontWeight: 800, minWidth: 170 }}
    >
      {releasingId === orderToRelease?.id ? (
        <CircularProgress size={20} color="inherit" />
      ) : (
        "Yes,Release Order"
      )}
    </Button>
  </DialogActions>
</Dialog>
    </PageShell>
  );
}


